import { useCallback, useContext } from "react";
import { useHistory, useRouteMatch } from "react-router";

import { type BuilderInfo } from "helpers/AppProvider.types";
import { SpaInfoContext } from "helpers/globalContext/SPAInfoContext";

import { CostType, MediaType, OpenActivityType } from "types/enum";

import { jobDetailRoute } from "utilities/flatRoutes/job.routes";
import { getBaseRoute, routes } from "utilities/routes";

import { AiSearchResultCard } from "./AiSearchResultCard";
import { getHitKey, normalizeKey } from "./entityMeta";
import type { ICategorizedHit, ISearchHit } from "./types";

const OPTION_ID_PREFIX = "ai-search-option-";
const EXCLUDED_ENTITIES = new Set<string>([]);

interface IAiSearchResultsProps {
    results: ICategorizedHit[];
    /**
     * PR-A1 (review 1511768): late-op hits rendered BELOW `results`, inside
     * the SAME `role="listbox"`, as a `role="group"` labeled "More results".
     * One listbox keeps `aria-activedescendant` containment valid for every
     * option the combobox can highlight — a second sibling listbox would put
     * appended options outside the `aria-controls` target. Option ids continue
     * sequentially after the committed rows, so `highlightedIndex` (an overall
     * index) and click telemetry span both sections naturally.
     */
    appendedResults?: ICategorizedHit[];
    query: string;
    highlightedIndex: number;
    pendingKey: string | null;
    onResultClick: (hit: ICategorizedHit, index: number) => void;
    /**
     * Owned by the caller because `commonComponents` may not read builder flags
     * directly. Pass `useContext(BuilderInfoContext)` from the parent.
     */
    builderInfo?: BuilderInfo | null;
    /**
     * graft/A6.4: render the applied-filter facets on each card and make them
     * clickable. Opt-in — the 640px dropdown has no room for them and stays
     * pill-free until A6.6 retires it, which keeps its visual baselines out of
     * this stage.
     */
    showFacets?: boolean;
    onFacetClick?: (facetKey: string) => void;
}

/**
 * Navigation strategy for a search result link.
 *
 * - "relative" — opens as a modal overlay via SearchBarRoutes.
 *   Path includes the /SearchResult prefix and is pushed relative to match.url.
 *   Matches the RelativeLink behaviour in the legacy GlobalSearch.
 *
 * - "direct" — navigates to the entity's full page (AutoSPARouting).
 *   Used for entities that the legacy GlobalSearch routed directly.
 */
type NavType = "relative" | "direct";

interface IEntityLink {
    path: string;
    navType: NavType;
}

function relativeLink(path: string): IEntityLink {
    return { path: `${routes.search.resultModalPrefix}${path}`, navType: "relative" };
}

function directLink(path: string): IEntityLink {
    return { path, navType: "direct" };
}

/**
 * Build a navigation link for a search result entity.
 *
 * Mirrors the legacy GlobalSearch link-type assignments:
 *   - RelativeLink entities open as modals via SearchBarRoutes
 *   - AutoSPARouting entities navigate directly to their detail page
 *   - Entities not in legacy search default to AutoSPARouting if functioning
 */
function getEntityLink(hit: ISearchHit, builderInfo?: BuilderInfo): IEntityLink | null {
    const jobId =
        Number(hit.payload?.jobId ?? hit.payload?.job_id ?? hit.payload?.jobsite_id) || null;
    const entity = normalizeKey(hit.entity);

    const id = Number(hit.id);
    if (isNaN(id)) return null;

    // ── Non-job-scoped entities ─────────────────────────────────────────
    switch (entity) {
        case "contact":
            return relativeLink(routes.contact.getDetailsLink(id, false));
        case "lead":
            return relativeLink(routes.lead.getDetailsLink(id));
        case "sub":
            return relativeLink(routes.sub.getDetailsLink(id));
        case "internaluser":
            return relativeLink(routes.internalUsers.getInternalUserDetailsLink(id));
        case "email":
        case "emailmessage":
            return relativeLink(routes.messages.getMessageDetailsLink(id));

        case "job":
        case "jobsite":
            return directLink(
                jobDetailRoute.link({ jobId: id }, undefined, { usePrevious: false }) as string,
            );

        case "task": {
            const externalId = hit.payload?.externalId;
            if (typeof externalId !== "string" || !externalId) return null;
            return relativeLink(routes.task.getDetailsLink(externalId));
        }

        case "costcode":
            return relativeLink(routes.costCode.getCostCodeDetailsLink(id, CostType.Both));

        case "leadactivity": {
            const leadId = Number(hit.payload?.leadId ?? hit.payload?.lead_id);
            if (isNaN(leadId)) return null;
            return relativeLink(
                routes.leadActivity.getDetailsLink(id, leadId, OpenActivityType.Edit, undefined),
            );
        }
    }

    // ── Job-scoped entities ─────────────────────────────────────────────
    if (!jobId) return null;

    switch (entity) {
        case "schedule":
        case "scheduleitem":
            return relativeLink(routes.schedule.getDetailsLink(id, jobId));
        case "purchaseorder":
            return relativeLink(routes.purchaseOrder.getDetailsLink(id, jobId));
        case "selection":
            return relativeLink(routes.selection.getDetailsLink(id, jobId));
        case "allowance":
            return relativeLink(routes.allowance.getDetailsLink(id, jobId));
        case "bid":
            return relativeLink(routes.bid.getDetailsLink(id, jobId));
        case "bidpackage":
            return relativeLink(routes.bidPackage.getDetailsLink(id, jobId));
        case "bill":
            if (builderInfo?.flags.billsFullPageConversion) {
                return directLink(routes.bill.getDetailsLink(id, jobId));
            }
            return relativeLink(routes.bill.getDetailsLink(id, jobId));
        case "warranty":
            if (builderInfo?.flags.warrantiesModalConversion) {
                return directLink(routes.warranty.getDetailsLink(id, jobId));
            }
            return relativeLink(routes.warranty.getDetailsLink(id, jobId));
        case "rfi":
            return relativeLink(routes.rfi.getDetailsLink(id, jobId));
        case "ownerinvoice":
        case "customerinvoice":
            return relativeLink(routes.ownerInvoice.getDetailsLink(id, jobId));
        case "document":
            return relativeLink(routes.media.getMediaPropertiesLink(id, MediaType.Document, jobId));
        case "photo":
            return relativeLink(routes.media.getMediaPropertiesLink(id, MediaType.Photo, jobId));
        case "video":
            return relativeLink(routes.media.getMediaPropertiesLink(id, MediaType.Video, jobId));
        case "proposal":
            return relativeLink(routes.lead.getDetailsLink(id));

        // AutoSPARouting in legacy search
        case "changeorder":
            return directLink(routes.changeOrder.getChangeOrderDetailsFullPage(id, jobId));
        case "dailylog":
            return directLink(routes.dailyLog.getDetailsLink(id, jobId));

        case "selectionchoice":
            return relativeLink(routes.selectionChoice.getDetailsLink(id, jobId));
        case "deposit":
            return relativeLink(routes.deposits.getDetailsLink(id, jobId));
        case "creditmemo":
            return relativeLink(routes.creditMemos.getDetailsLink(id, jobId));
        case "submittal": {
            const externalId = hit.payload?.externalId;
            if (typeof externalId !== "string" || !externalId) return null;
            return directLink(routes.submittal.getDetailsLink(externalId, jobId));
        }
        default:
            return null;
    }
}

/**
 * The production definition of a VISIBLE search hit: not client-excluded and
 * routable (getEntityLink resolves without builder flags). AiSearch.tsx filters
 * both the live and stale result lists through this, and the SSE replay harness
 * (sseReplayHarness/replayAiSearch.ts) measures list stability through the SAME
 * predicate — so the harness's notion of "visible" cannot silently drift from
 * what the user actually sees.
 */
const isVisibleHit = (hit: ISearchHit): boolean =>
    !EXCLUDED_ENTITIES.has(normalizeKey(hit.entity)) && getEntityLink(hit) !== null;

function navigateEntityLink(
    link: IEntityLink,
    history: { push: (path: string) => void },
    matchUrl: string,
    isSpa: boolean,
) {
    if (link.navType === "relative") {
        history.push(matchUrl.replace(/\/$/, "") + link.path);
    } else if (isSpa) {
        history.push(link.path);
    } else {
        window.location.assign(getBaseRoute() + link.path);
    }
}

export { EXCLUDED_ENTITIES, getEntityLink, isVisibleHit, navigateEntityLink, OPTION_ID_PREFIX };

// Stable default for the optional appendedResults prop — an inline [] default
// would be a new array identity every render (@eslint-react/no-unstable-default-props).
const NO_APPENDED_RESULTS: ICategorizedHit[] = [];

export function AiSearchResults(props: IAiSearchResultsProps) {
    const {
        results,
        appendedResults = NO_APPENDED_RESULTS,
        query,
        highlightedIndex,
        pendingKey,
        onResultClick,
        builderInfo,
        showFacets = false,
        onFacetClick,
    } = props;
    const history = useHistory();
    const match = useRouteMatch();
    const spaContext = useContext(SpaInfoContext);

    const handleClick = useCallback(
        (hit: ICategorizedHit, index: number) => {
            const link = getEntityLink(hit, builderInfo ?? undefined);
            if (!link) return;

            navigateEntityLink(link, history, match.url, !!spaContext.isSpa);
            onResultClick(hit, index);
        },
        [history, match.url, spaContext.isSpa, onResultClick, builderInfo],
    );

    const renderCard = (hit: ICategorizedHit, overallIndex: number) => {
        const key = getHitKey(hit);
        return (
            <AiSearchResultCard
                key={key}
                hit={hit}
                query={query}
                isHighlighted={overallIndex === highlightedIndex}
                isPending={key === pendingKey}
                elementId={`${OPTION_ID_PREFIX}${overallIndex}`}
                onClick={() => handleClick(hit, overallIndex)}
                showFacets={showFacets}
                onFacetClick={onFacetClick}
            />
        );
    };

    return (
        <div
            className="ai-search-results"
            role="listbox"
            id="ai-search-listbox"
            data-testid="ai-search-results"
        >
            {results.map((hit, i) => renderCard(hit, i))}
            {appendedResults.length > 0 && (
                <div
                    className="ai-search-appended"
                    role="group"
                    aria-label="More results"
                    data-testid="ai-search-appended"
                >
                    <div
                        className="ai-search-appended-header"
                        role="presentation"
                        data-testid="ai-search-appended-header"
                    >
                        More results
                    </div>
                    <div data-testid="ai-search-appended-results">
                        {appendedResults.map((hit, i) => renderCard(hit, i + results.length))}
                    </div>
                </div>
            )}
        </div>
    );
}
